<script setup lang="ts">
import YscIconsArrowLeft from './YscIconsArrowLeft.vue'
import YscIconsArrowRight from './YscIconsArrowRight.vue'
import { useRoute, computed, useNuxtApp } from '#imports'

interface Props {
  count: number
  limit?: number
  pageSize?: number
}

const props = withDefaults(defineProps<Props>(), {
  limit: 5,
  pageSize: 20,
})

const route = useRoute()
const {
  $i18n: { t },
} = useNuxtApp()

const currentPage = computed(() => {
  if (route.query.page) {
    return Array.isArray(route.query.page) && route.query.page[0] ? +route.query.page[0] : +route.query.page
  }
  return 1
})

const pageValues = computed(() =>
  [...Array(Math.ceil(props.count / props.pageSize)).keys()].map(v => ({
    val: v + 1,
    current: v + 1 === +currentPage.value,
    path: {
      query: { ...route.query, page: v + 1 },
    },
    title: t('pagination.page_current_dynamic', {
      current_number: v + 1,
    }),
  })),
)

const cells = computed(() => pageValues.value.slice(firstCellIndex.value, showExtra.value ? lastCellIndex.value - 1 : lastCellIndex.value))

const firstCellIndex = computed(() => Math.max(Math.ceil(currentPage.value - 1 - props.limit / 2), 0))

const lastCellIndex = computed(() => (firstCellIndex.value === 0 ? props.limit : Math.ceil(currentPage.value - 1 + props.limit / 2)))

const showExtra = computed(() => lastCellIndex.value < pageValues.value.length)

const previousCell = computed(() => ({
  path: currentPage.value !== 1 ? { query: { ...route.query, page: currentPage.value - 1 } } : undefined,
  title: t('pagination.page_previous'),
}))

const nextCell = computed(() => ({
  path: currentPage.value !== pageValues.value.length ? { query: { ...route.query, page: currentPage.value + 1 } } : undefined,
  title: t('pagination.page_next'),
}))
</script>

<template>
  <div class="flex items-center justify-between sm:justify-center">
    <div class="text-sm text-gray-500 sm:hidden">
      {{
        $t('pagination.summary_dynamic', {
          current_number: currentPage,
          total_number: pageValues.length,
        })
      }}
    </div>

    <div class="flex items-center justify-center">
      <nav
        class="relative z-0 inline-flex space-x-1"
        aria-label="Pagination"
      >
        <NuxtLink
          :to="previousCell.path"
          :title="previousCell.title"
          class="relative hidden h-10 w-10 items-center justify-center rounded-full leading-none hover:bg-gray-100 sm:flex"
          :class="[previousCell.path ?? 'cursor-not-allowed text-gray-400']"
        >
          <YscIconsArrowLeft class="h-5 w-5" />
        </NuxtLink>
        <NuxtLink
          v-for="cell in cells"
          :key="cell.title"
          :to="cell.path"
          class="relative flex h-10 w-10 items-center justify-center rounded-full leading-none"
          :class="[cell.current ? 'bg-pink-50 text-pink-500 hover:bg-pink-50' : 'cursor-pointer text-gray-500 hover:bg-gray-100']"
          :title="cell.title"
        >
          {{ cell.val }}
        </NuxtLink>
        <span
          v-if="showExtra"
          class="relative hidden h-10 w-10 cursor-not-allowed items-center justify-center rounded-full leading-none text-gray-400 sm:flex"
        >…</span>

        <NuxtLink
          :to="nextCell.path"
          :title="nextCell.title"
          class="relative hidden h-10 w-10 items-center justify-center rounded-full leading-none hover:bg-gray-100 sm:flex"
          :class="[nextCell.path ?? 'cursor-not-allowed text-gray-400']"
        >
          <YscIconsArrowRight class="h-5 w-5" />
        </NuxtLink>
      </nav>
    </div>
  </div>
</template>
